import type {
  AdditionalPointsPurchase,
  Flights,
  MulticityFlights,
  TrackingProperties,
} from "@b2bportal/air-shopping-api";
import type { FlightsShopState as CoreFlightsShopState } from "@b2bportal/core-flights";
import {
  CallState,
  FlightShopStep,
  MulticityFlightShopStep,
  SortOptionEnum,
} from "@b2bportal/core-types";

export type PricingMode = "fixed" | "flex";

/**
 * RBC-specific flight shop state extending core shop state
 * Adds fixed/flex pricing modes and additional points purchase
 */
export interface FlightsShopState extends CoreFlightsShopState {
  fixedPricingFlights?: Flights;
  flexPricingFlights?: Flights;
  fixedPricingMulticityFlights?: MulticityFlights;
  flexPricingMulticityFlights?: MulticityFlights;
  fixedPricingFlightsCallState: CallState;
  flexPricingFlightsCallState: CallState;
  fixedPricingFlightsError: boolean;
  flexPricingFlightsError: boolean;
  /**
   * Coarse: fixed-points shop call failed (any reason). Used for tabs / flex vs fixed UX.
   * @see getIsSearchedRouteUnavailableForGridPurchase
   */
  routeNotAllowedForFixedPointsPricing: boolean | undefined;
  /**
   * Refined: last fixed-points shop summary failed with error code
   * `RouteNotAllowedForFixedPointsPricing` (route/destination grid rules only).
   * @see getIsShopSummaryDestinationIneligibleForGridPricing
   */
  shopSummaryDestinationIneligibleForGridPricing: boolean | undefined;
  /**
   * Refined: last fixed-points shop summary failed with error code
   * `InsufficientPointsBalanceForFixedPointsPricing` (user has insufficient points).
   * @see getIsShopSummaryInsufficientPointsBalance
   */
  shopSummaryInsufficientPointsBalance: boolean | undefined;
  pricingMode: PricingMode;
  additionalPointsPurchase?: AdditionalPointsPurchase;
  /**
   * Fixed pricing tracking properties from shop summary response
   * Contains grid-specific metadata like grid SKU (route type)
   */
  fixedPricingTrackingProperties?: TrackingProperties;
}

/**
 * Initial state for flight shop
 */
export const initialShopState: FlightsShopState = {
  flights: undefined,
  flightsCallState: CallState.NotCalled,
  flightsError: false,
  selectedTrip: {
    tripId: "",
    outgoingSliceId: "",
    outgoingFareId: "",
  },
  prediction: undefined,
  tripDetailsById: {},
  sortOption: SortOptionEnum.FareScore,
  shopStep: FlightShopStep.departure,
  fixedPricingFlights: undefined,
  flexPricingFlights: undefined,
  fixedPricingMulticityFlights: undefined,
  flexPricingMulticityFlights: undefined,
  fixedPricingFlightsError: false,
  flexPricingFlightsError: false,
  routeNotAllowedForFixedPointsPricing: true,
  shopSummaryDestinationIneligibleForGridPricing: undefined,
  shopSummaryInsufficientPointsBalance: undefined,
  pricingMode: "flex",
  fixedPricingFlightsCallState: CallState.NotCalled,
  flexPricingFlightsCallState: CallState.NotCalled,
  selectedMulticityTrips: [
    { tripId: null, outgoingSliceId: null, outgoingFareId: null },
    { tripId: null, outgoingSliceId: null, outgoingFareId: null },
  ],
  multicityShopStep: MulticityFlightShopStep.departure0,
  fixedPricingTrackingProperties: undefined,
};
